import React, { useEffect } from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';

import { AuthProvider, useAuth } from './auth/AuthContext';
import apiClient from './api';
import useAutoLogout from "./hooks/useAutoLogout";

import Register from './components/Auth/Register';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import Navbar from './components/Navbar';
import MyAccount from './pages/MyAccount';
import PlayerProfile from './pages/PlayerProfile';
import MatchDayDetails from './pages/MatchDayDetails';
import CreateMatchDay from './pages/CreateMatchDay';
import SeasonManager from './pages/SeasonManager';
import PrivateRoute from './components/PrivateRoute';
import AmericanasList from './pages/AmericanasList';
import CreateAmericana from './pages/CreateAmericana';
import AmericanaDetails from './pages/AmericanaDetails';
import EditAmericana from './pages/EditAmericana';
import Socias from './pages/Socias';
import PairStats from './pages/PairStats';

function AppRoutes() {
    const { user } = useAuth();

    const roles = Array.isArray(user?.roles) ? user.roles.map(r => String(r).replace(/^ROLE_/, '').toUpperCase()) : [];
    const isAuthenticated = !!user;
    const isAdmin = roles.some(r => ['ADMIN', 'COORDINATOR', 'CAPTAIN'].includes(r));
    const isCaptain = roles.includes('CAPTAIN');

    useAutoLogout(isAuthenticated);

    return (
        <Router>
            <Navbar isAuthenticated={isAuthenticated} isAdmin={isAdmin} isCaptain={isCaptain} />
            <Routes>
                {/* Auth */}
                <Route
                    path="/"
                    element={
                        !isAuthenticated
                            ? <Login />
                            :  <Navigate to="/dashboard" replace />
                    }
                />
                <Route
                    path="/login"
                    element={<Login />}
                />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>}/>
                <Route path="/account" element={<PrivateRoute><MyAccount /></PrivateRoute>} />
                <Route path="/players/:id" element={<PrivateRoute><PlayerProfile /></PrivateRoute>} />
                <Route path="/matchdays/:id" element={<PrivateRoute><MatchDayDetails isAdmin={isAdmin} /></PrivateRoute>} />
                <Route path="/matchdays/new" element={<PrivateRoute><CreateMatchDay /></PrivateRoute>} />
                <Route path="/seasons" element={<PrivateRoute>{isAdmin ? <SeasonManager /> : <Navigate to="/dashboard" replace />}</PrivateRoute>} />

                <Route
                    path="/socias"
                    element={<PrivateRoute>{isAdmin ? <Socias /> : <Navigate to="/" replace />}</PrivateRoute>}
                />

                {/* Americana */}
                <Route path="/americanas" element={<PrivateRoute><AmericanasList canManage={isAdmin} /></PrivateRoute>} />
                <Route path="/americanas/new" element={<PrivateRoute>{isAdmin ? <CreateAmericana /> : <Navigate to="/americanas" replace />}</PrivateRoute>} />
                <Route path="/americanas/:id" element={<PrivateRoute><AmericanaDetails roles={roles} /></PrivateRoute>} />
                <Route path="/americanas/:id/edit" element={<PrivateRoute>{isAdmin ? <EditAmericana /> : <Navigate to="/americanas" replace />}</PrivateRoute>} />

                <Route path="/estadisticas" element={<PrivateRoute>{isCaptain ? <PairStats /> : <Navigate to="/dashboard" replace />}</PrivateRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

export default function App() {
    // Ping al backend nada más cargar la app para que Render despierte
    // antes de que el usuario llegue a hacer login
    useEffect(() => {
        apiClient.get('/ping').catch(() => {});
    }, []);

    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    );
}
